import "@tento-ui/components/rich-text-editor/rich-text-editor";
import "@tento-ui/brand/blog/blog-editorial-post-pagelet";
import "@tento-ui/components/squircle/squircle";
import "@tento-ui/components/code/code";

import { BlogEditorialPostPagelet } from "@tento-ui/brand/blog/blog-editorial-post-pagelet";
import { CodeBlockView } from "@tento-ui/components/code/code-block-view";
import { RichTextEditor } from "@tento-ui/components/rich-text-editor/rich-text-editor";
import { Squircle } from "@tento-ui/components/squircle/squircle";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Lazy } from "@tento-core/lazy";
import { Prosemirror } from "@tento-ui/ui/prosemirror-import";
import type { BlogPost } from "../blog-data";
import type {
  BlogPostPage,
  BlogPostRoute,
} from "../blog-variant";

// Rich markdown serde for post bodies: tables + task lists + std-code code
// blocks. Built once, lazily (the prosemirror bundle loads on demand).
const blogPostSerde = new Lazy(async () => {
  const pm = await Prosemirror.get();
  return pm.Markdown.buildSchema([], { fileRefs: false });
});

export const editorialPostRoute: BlogPostRoute = {
  make(post: BlogPost, delegate: BlogPostPage.Delegate): HTMLElement {
    // Hero image — squircle-clipped
    const $heroImg = document.createElement("img");
    $heroImg.src = post.heroImage;
    $heroImg.alt = post.title;
    $heroImg.slot = "clipped";
    $heroImg.style.width = "100%";
    $heroImg.style.height = "100%";
    $heroImg.style.objectFit = "cover";
    $heroImg.style.display = "block";

    const $hero = Squircle.makeWith({
      radius: 24,
      smoothing: 0.6,
      fill: "transparent",
    });
    $hero.assignStyles({ width: "100%", height: "480px" });
    $hero.appendChild($heroImg);

    // Content
    const $content = RichTextEditor.make() as RichTextEditor;
    // Render with the rich markdown serde (tables + task lists + std-code) and
    // highlight fenced code via std-code (CodeMirror/Lezer), like
    // std-documentation. Inline `code` stays a plain <code>. The serde resolves
    // async (the prosemirror bundle loads on demand), so bind once it is ready.
    void blogPostSerde.get().then((serde) => {
      $content.bind({
        markdown: post.content ?? "",
        editable: false,
        serde,
        nodeViews: { code_block: (node) => new CodeBlockView(node) },
      });
    });

    // Back link
    const $back = document.createElement("div");
    $back.classList.add("back-link");
    $back.append(
      Object.assign(document.createElement("span"), {
        textContent: "\u2190",
      }),
      Object.assign(document.createElement("span"), {
        textContent: "Back to blog",
      }),
    );
    GESTURE_MANAGER.addPointerEvent($back, {
      onClick: () => delegate.onBack(),
    });

    return BlogEditorialPostPagelet.makeWith({
      heroImage: $hero,
      content: $content,
      back: $back,
    });
  },
  bind(pagelet: HTMLElement, _post: BlogPost, _delegate: BlogPostPage.Delegate): HTMLElement {
    return pagelet;
  },
};
